import DefaultLayout from '../../components/DefaultLayout';
import { Form } from '../../../ui/Form/Form';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Button, Icon, Spinner } from '../../../ui';
import { Pagination } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ProductsTopFilter } from '../../../ui/Form/filters/ProductsTopFilter';
import { options } from '../../../config/config';
import { StyledAllFarmsPage } from './style';
import { FarmData } from '../../../types/FarmData';
import { AllFarmsFarmCard } from '../../../ui/Card/AllFarmsFarmCard';
import { Link, useNavigate } from 'react-router-dom';
import Banner from 'public/components/DefaultLayout/Banner';
import { useBannerState } from 'public/context';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { StyledMobileFilterFooter } from 'public/components/MobileLayout/MobileMenu/MobileFilterFooter/style';
import MobileFilter from 'public/components/MobileLayout/MobileFilter/MobileFilter';
import { FilterOptions } from 'types/FilterOptions';
import { Filter } from 'types/Filter';
import {
  useFilterDispatch,
  useFilterState,
} from 'public/context/FilterContext';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';
import { paginationNavigationRenderer } from 'utils/paginationNavigationrenderer';
import { useFarmDispatch } from 'public/context/FarmContext';
import { FarmCoordinates } from 'types/FarmData';

export const AllFarms = () => {
  const [filter, setFilter] = useState<Filter>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [farms, setFarms] = useState<FarmData[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  const navigation = useNavigate();
  const dispatchFilter = useFilterDispatch();
  const { isOpenMobileFilter, isOpenMobileMenu } = useFilterState();
  const dispatchFarm = useFarmDispatch();

  const openCloseDrawer = useCallback(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileFilter: !isOpenMobileFilter },
    });
  }, [isOpenMobileFilter]);

  useEffect(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isFilter: true, openCloseDrawer },
    });
  }, [dispatchFilter, openCloseDrawer]);

  const handleSubmitFilter = () => {
    openCloseDrawer();
    appendData({
      filter: filter,
      page: currentPage,
      page_size: pageSize,
    });
  };

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/farms',
      data: {
        page: currentPage,
        page_size: pageSize,
        ...filter,
      },
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setTotal(response.farms.total);
      setFarms(response.farms.data);
      setFilterOptions(response.filters);

      if (response.farmsCoordinates) {
        dispatchFarm({
          type: 'SAVE_PAYLOAD',
          payload: {
            farmsCoordinates: response.farmsCoordinates as FarmCoordinates[],
            refetch: () => {},
          },
          isLoading: false,
        });
      }
    },
  });

  useEffect(() => {
    appendData({
      filter: filter,
      page: currentPage,
      page_size: pageSize,
    });
  }, [filter, currentPage, pageSize]);

  const handleFilterChange = (newFilter: Filter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleShowMap = () => {
    navigation('/map', {
      state: {
        filters: filter,
        dataType: 'farms',
        page: currentPage,
        page_size: pageSize,
        farms: farms,
        total: total,
      },
    });
  };

  const intl = useIntl();

  const pageSizeOptions = options.filters.pageSizeOptions;

  const breadcrumbs = [
    {
      route: '/',
      name: intl.formatMessage({ id: 'breadcrumbs.home' }),
    },
    {
      route: 'all',
      name: intl.formatMessage({ id: 'breadcrumbs.farms' }),
    },
  ];

  const { banners } = useBannerState();
  const banner_4_data = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );

  const drawer = () => (
    <MobileMenuDrawer
      title={
        <div>
          <p className="title">
            {intl.formatMessage({ id: 'farms.filter' })}
          </p>
          <div className={'count-title'}>
            {total} {intl.formatMessage({ id: 'farms.farms_and_producers' })}
          </div>
        </div>
      }
      onClose={openCloseDrawer}
      open={isOpenMobileFilter}
      className="mobile-filter"
      footer={
        <StyledMobileFilterFooter>
          <Button
            label={intl.formatMessage({ id: 'general.select' })}
            icon={<Icon name={'mobile-filter-submit-arrow-icon'} />}
            onClick={handleSubmitFilter}
          />
        </StyledMobileFilterFooter>
      }
      closable={false}
    >
      <MobileFilter
        setFilter={setFilter}
        filter={filter}
        filterOptions={filterOptions}
      />
    </MobileMenuDrawer>
  );

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent>
        <StyledAllFarmsPage>
          <DefaultThirdHeader
            label={intl.formatMessage({ id: 'form.farms_page_title' })}
            filters={
              <ProductsTopFilter
                total={total}
                per_page={pageSize}
                setFilter={(newFilter) =>
                  handleFilterChange({ ...filter, ...newFilter })
                }
                page_size_options={pageSizeOptions}
                setPageSize={setPageSize}
                unit={'farms'}
                filters={filter}
                handleShowMap={handleShowMap}
              />
            }
            breadcrumb={breadcrumbs}
          />
          <SearchAndFilter
            rootClassName={`laptop-search-section-box ${
              isOpenMobileMenu && 'mobile-search-fixed'
            }`}
            scrollThreshold={310}
          />
          <Spinner spinning={isLoading}>
            <div className={'products-filters-container'}>
              <Form
                setFilter={handleFilterChange}
                filterOptions={filterOptions}
                filter={filter}
              />
              <div className={'products-wrapper'}>
                <div className={'products'}>
                  {farms.map((farm: FarmData, index) => (
                    <Link to={`/farm/${farm.id}`} key={`${index}-lnk`}>
                      <AllFarmsFarmCard
                        name={farm.brand ?? farm.name}
                        key={`frm-crd-${index}`}
                        imageUrl={farm.logo}
                        qmarks={farm.quality_marks}
                        registeredFullAddress={farm.registered_full_address}
                        is_top_farm={farm.is_top_farm}
                      />
                    </Link>
                  ))}
                </div>
                <div className={'pagination'}>
                  {total > pageSize && (
                    <Pagination
                      total={total}
                      showSizeChanger={false}
                      current={currentPage}
                      defaultPageSize={20}
                      pageSize={pageSize}
                      onChange={(page, pageSize) => {
                        setPageSize(pageSize);
                        setCurrentPage(page);
                      }}
                      itemRender={paginationNavigationRenderer}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="banners">
              <div className="container">
                {banner_4_data && (
                  <Banner banners={[banner_4_data]} className="desktop-banner" />
                )}
              </div>
            </div>
          </Spinner>
        </StyledAllFarmsPage>
        {drawer()}
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
